import React, { useState, useEffect, useCallback } from 'react';
import styles from './QRCodePopup.module.css';

interface QRCodePopupProps {
  isOpen: boolean;
  onClose: () => void;
  affiliateId: string;
  username?: string;
}

const QRCodePopup: React.FC<QRCodePopupProps> = ({
  isOpen,
  onClose,
  affiliateId,
  username,
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const generateQRCode = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { QRCodeService } = await import('../../services/qrCode.service');
      const qrService = QRCodeService.getInstance();

      // Generate QR code with affiliate ID
      const dataUrl = await qrService.generateQRCode(affiliateId);
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      console.error('Error generating QR code:', err);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [affiliateId]);

  useEffect(() => {
    if (isOpen && affiliateId) {
      generateQRCode();
    }
  }, [isOpen, affiliateId, generateQRCode]);

  const handleDownload = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.href = qrCodeDataUrl;
      link.download = `affiliate-qr-${affiliateId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(affiliateId);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div
        className={styles.modalContainer}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>

        <div className={styles.modalContent}>
          <h2 className={styles.modalTitle}>Affiliate QR Code</h2>

          <div className={styles.affiliateInfo}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Affiliate ID:</span>
              <span className={styles.value}>{affiliateId}</span>
              <button
                className={styles.copyBtn}
                onClick={handleCopyToClipboard}
                title="Copy to clipboard"
              >
                📋
              </button>
            </div>
            {username && (
              <div className={styles.infoRow}>
                <span className={styles.label}>Username:</span>
                <span className={styles.value}>{username}</span>
              </div>
            )}
          </div>

          <div className={styles.qrContainer}>
            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Generating QR Code...</p>
              </div>
            ) : error ? (
              <div className={styles.error}>
                <p>{error}</p>
                <button className={styles.retryBtn} onClick={generateQRCode}>
                  Retry
                </button>
              </div>
            ) : (
              <>
                <img
                  src={qrCodeDataUrl}
                  alt={`QR Code for Affiliate ID: ${affiliateId}`}
                  className={styles.qrCode}
                />
                <p className={styles.qrDescription}>
                  Scan this QR code to get the affiliate information
                </p>
              </>
            )}
          </div>

          <div className={styles.actionButtons}>
            <button
              className={styles.downloadBtn}
              onClick={handleDownload}
              disabled={!qrCodeDataUrl || loading}
            >
              Download QR Code
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodePopup;
